/**
 * SFPageSpeedBooster — Intelligent link & resource prefetcher
 * Strategies: hover intent, viewport intersection, idle-time, mouseover
 * Usage: new SFPageSpeedBooster({ strategy: 'hybrid', debug: true })
 *
 * Compatible: classic <script>, ES module import, CommonJS require()
 */
(function (factory) {
  var built = factory();
  // CommonJS / Node
  if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = built;
  }
  // ES module namespace (bundlers that expose import.meta)
  else if (typeof exports !== 'undefined') {
    exports.SFPageSpeedBooster = built;
    exports.default = built;
  }
  // Browser global — always write to window explicitly
  else {
    window.SFPageSpeedBooster = built;
  }
})(function () {
  'use strict';

  /* ─── Defaults ─────────────────────────────────────────────── */
  const DEFAULTS = {
    strategy: 'hybrid',         // 'hover' | 'viewport' | 'idle' | 'hybrid'
    hoverDelay: 65,             // ms before hover triggers prefetch
    intersectionThreshold: 0.1, // viewport visibility threshold
    intersectionRootMargin: '0px 0px 200px 0px', // start early (below fold)
    idleConcurrency: 2,         // links to prefetch per idle callback
    maxPrefetches: 50,          // hard cap on total prefetched URLs
    ignore: [],                 // extra URL patterns/functions to skip
    prefetchResources: true,    // also prefetch linked page's critical resources
    connectionAware: true,      // skip on slow/data-saver connections
    debug: false,
  };

  /* ─── Helpers ───────────────────────────────────────────────── */
  function log(msg, ...args) { 
    // console.log(`[PSB] ${msg}`, ...args);
  }

  function isSameOrigin(url) {
    try { return new URL(url, location.href).origin === location.origin; }
    catch { return false; }
  }

  function isValidLink(href) {
    if (!href) return false;
    if (/^(mailto:|tel:|javascript:|#)/.test(href)) return false;
    if (href.startsWith('#')) return false;
    return isSameOrigin(href);
  }

  function isSaveDataOrSlow() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return ['slow-2g', '2g'].includes(conn.effectiveType);
  }

  function normalisedUrl(href) {
    const url = new URL(href, location.href);
    url.hash = '';
    return url.href;
  }

  /* ─── DOM injection helpers ─────────────────────────────────── */
  function injectLinkTag(url, rel = 'prefetch') {
    if (document.querySelector(`link[rel="${rel}"][href="${url}"]`)) return false;
    const link = document.createElement('link');
    link.rel = rel;
    link.href = url;
    link.fetchPriority = 'low';
    document.head.appendChild(link);
    return true;
  }

  function injectDNSPrefetch(origin) {
    const key = `link[rel="dns-prefetch"][href="${origin}"]`;
    if (!document.querySelector(key)) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = origin;
      document.head.appendChild(link);
    }
  }

  /* ─── Core class ────────────────────────────────────────────── */
  function SFPageSpeedBooster(userOptions) {
    this.opts = Object.assign({}, DEFAULTS, userOptions);
    this.prefetched = new Set();
    this.hoverTimers = new WeakMap();
    this._io = null;
    this._idleQueue = [];
    this._idleHandle = null;
    this._bound = {};

    if (typeof requestIdleCallback === 'undefined') {
      // Polyfill for Safari
      window.requestIdleCallback = (cb) => setTimeout(cb, 1);
      window.cancelIdleCallback = clearTimeout;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  SFPageSpeedBooster.prototype = {

    init() {
      if (this.opts.connectionAware && isSaveDataOrSlow()) {
        this.opts.debug && log('Slow/data-saver connection detected — prefetching disabled.');
        return;
      }

      // Preconnect to known CDN origins found on page
      this._preconnectExternalOrigins();

      const s = this.opts.strategy;
      if (s === 'hover'    || s === 'hybrid') this._attachHover();
      if (s === 'viewport' || s === 'hybrid') this._attachIntersection();
      if (s === 'idle'     || s === 'hybrid') this._scheduleIdle();

      this.opts.debug && log(`Initialised — strategy: "${s}"`);
    },

    /* ─── Public API ─────────────────────────────── */

    /** Manually prefetch a URL */
    prefetch(url, rel = 'prefetch') {
      const resolved = normalisedUrl(url);
      if (!this._canPrefetch(resolved)) return false;
      const injected = injectLinkTag(resolved, rel);
      if (injected) {
        this.prefetched.add(resolved);
        this.opts.debug && log(`Prefetched (${rel}):`, resolved);
      }
      return injected;
    },

    /** Preload a resource immediately (high priority) */
    preload(url, as) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      if (as) link.as = as;
      document.head.appendChild(link);
      this.opts.debug && log(`Preloaded (${as || '?'}):`, url);
    },

    /** Prefetch all links in a container */
    prefetchContainer(el) {
      const links = (el || document).querySelectorAll('a[href]');
      links.forEach(a => this.prefetch(a.href));
    },

    /** Destroy all listeners */
    destroy() {
      const el = document.body;
      el.removeEventListener('mouseover', this._bound.over);
      el.removeEventListener('mouseout', this._bound.out);
      el.removeEventListener('touchstart', this._bound.touch);
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._idleHandle) { cancelIdleCallback(this._idleHandle); }
      this.opts.debug && log('Destroyed.');
    },

    /* ─── Internal ───────────────────────────────── */

    _canPrefetch(url) {
      if (!url || !isValidLink(url)) return false;
      if (this.prefetched.has(url)) return false;
      if (this.prefetched.size >= this.opts.maxPrefetches) return false;
      if (url === normalisedUrl(location.href)) return false;

      // User-defined ignore rules
      for (const rule of this.opts.ignore) {
        if (typeof rule === 'function' && rule(url)) return false;
        if (rule instanceof RegExp && rule.test(url)) return false;
        if (typeof rule === 'string' && url.includes(rule)) return false;
      }
      return true;
    },

    /* ── Strategy 1: Hover intent ──────────────────── */
    _attachHover() {
      this._bound.over = this._onOver.bind(this);
      this._bound.out  = this._onOut.bind(this);
      this._bound.touch = this._onTouch.bind(this);

      document.body.addEventListener('mouseover', this._bound.over, { passive: true });
      document.body.addEventListener('mouseout',  this._bound.out,  { passive: true });
      document.body.addEventListener('touchstart', this._bound.touch, { passive: true });
    },

    _closestLink(el) {
      return el && el.closest ? el.closest('a[href]') : null;
    },

    _onOver(e) {
      const a = this._closestLink(e.target);
      if (!a) return;
      const url = normalisedUrl(a.href);
      if (!this._canPrefetch(url)) return;

      const timer = setTimeout(() => {
        this.prefetch(url);
      }, this.opts.hoverDelay);
      this.hoverTimers.set(a, timer);
    },

    _onOut(e) {
      const a = this._closestLink(e.target);
      if (!a) return;
      const timer = this.hoverTimers.get(a);
      if (timer) { clearTimeout(timer); this.hoverTimers.delete(a); }
    },

    _onTouch(e) {
      // On touch, prefetch immediately on touchstart (no delay)
      const a = this._closestLink(e.target);
      if (!a) return;
      this.prefetch(normalisedUrl(a.href));
    },

    /* ── Strategy 2: Viewport intersection ─────────── */
    _attachIntersection() {
      if (!('IntersectionObserver' in window)) return;

      this._io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const a = entry.target;
          const url = normalisedUrl(a.href);
          if (this._canPrefetch(url)) {
            // Use 'prefetch' (next-navigation) not 'preload'
            this.prefetch(url, 'prefetch');
          }
          this._io.unobserve(a); // once is enough
        });
      }, {
        threshold: this.opts.intersectionThreshold,
        rootMargin: this.opts.intersectionRootMargin,
      });

      document.querySelectorAll('a[href]').forEach(a => {
        if (isValidLink(a.href)) this._io.observe(a);
      });

      // Re-observe dynamically added links via MutationObserver
      new MutationObserver((muts) => {
        muts.forEach(m => m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          const links = node.matches('a[href]')
            ? [node]
            : Array.from(node.querySelectorAll('a[href]'));
          links.forEach(a => { if (isValidLink(a.href)) this._io.observe(a); });
        }));
      }).observe(document.body, { childList: true, subtree: true });
    },

    /* ── Strategy 3: Idle-time queue ───────────────── */
    _scheduleIdle() {
      // Collect all links and batch-prefetch during idle periods
      const links = Array.from(document.querySelectorAll('a[href]'))
        .map(a => normalisedUrl(a.href))
        .filter(url => this._canPrefetch(url));

      // Deduplicate
      this._idleQueue = [...new Set(links)];
      this._runIdle();
    },

    _runIdle() {
      if (!this._idleQueue.length) return;
      this._idleHandle = requestIdleCallback((deadline) => {
        let batch = this.opts.idleConcurrency;
        while (batch-- > 0 && this._idleQueue.length) {
          const url = this._idleQueue.shift();
          if (this._canPrefetch(url)) this.prefetch(url);
        }
        if (this._idleQueue.length) this._runIdle();
      }, { timeout: 2000 });
    },

    /* ── Preconnect to external CDN origins ─────────── */
    _preconnectExternalOrigins() {
      const seen = new Set();
      document.querySelectorAll('[src],[href],[data-src]').forEach(el => {
        const url = el.src || el.href || el.dataset.src;
        if (!url) return;
        try {
          const origin = new URL(url).origin;
          if (origin !== location.origin && !seen.has(origin)) {
            seen.add(origin);
            const link = document.createElement('link');
            link.rel = 'preconnect';
            link.href = origin;
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
            injectDNSPrefetch(origin);
            this.opts.debug && log('Preconnect:', origin);
          }
        } catch {}
      });
    },
  };

  /* ─── Static helper: inject critical resource hints ─────────── */
  SFPageSpeedBooster.injectResourceHints = function (hints = {}) {
    const head = document.head;

    (hints.preconnect || []).forEach(url => {
      const l = document.createElement('link');
      l.rel = 'preconnect'; l.href = url; l.crossOrigin = 'anonymous';
      head.appendChild(l);
    });

    (hints.preload || []).forEach(({ url, as, type }) => {
      const l = document.createElement('link');
      l.rel = 'preload'; l.href = url;
      if (as) l.as = as;
      if (type) l.type = type;
      head.appendChild(l);
    });

    (hints.prefetch || []).forEach(url => {
      const l = document.createElement('link');
      l.rel = 'prefetch'; l.href = url;
      head.appendChild(l);
    });
  };

  return SFPageSpeedBooster;
});